import { TRangeDate } from 'Common/DatePicker';
import { callGetPresignFromS3 } from './api_helper';

const VIETNAM_TIMEZONE_OFFSET_HOURS = 7;
const VIETNAM_TIMEZONE_OFFSET_MS = VIETNAM_TIMEZONE_OFFSET_HOURS * 60 * 60 * 1000;
const LOCAL_DATE_ONLY_REGEX = /^(\d{4})-(\d{2})-(\d{2})$/;
const LOCAL_DATE_TIME_REGEX =
  /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,9}))?)?$/;
const ISO_WITH_TIMEZONE_REGEX =
  /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,9}))?)?(Z|[+-]\d{2}:?\d{2})$/;

export enum PERMISSIONS {
  READ = 1,
  CREATE = 2,
  UPDATE = 4,
  DELETE = 8,
  EXPORT = 16,
}

export const DEFAULT_FEATURES = [
  'DASHBOARD_INVENTORY',
  'DASHBOARD_DEBT',
  'CUSTOMER',
  'CUSTOMER_OBJECT',
  'ORDER',
  'RECEIPT',
  'PRODUCT',
  'PRODUCT_CATEGORY',
  'SERVICE',
  'SERVICE_CATEGORY',
  'INVENTORY_DOC',
  'SHIFT_MANAGEMENT',
  'SHIFT_MEMBERSHIP',
  'EXPENSE_VOUCHER',
  'EXPENSE_CATEGORY',
  'PUMP',
  'ACCOUNT_MANAGEMENT',
  'HISTORY',
];

export const FEATURE_LABELS: Record<string, string> = {
  DASHBOARD_INVENTORY: 'Báo cáo Vật tư',
  DASHBOARD_DEBT: 'Báo cáo công nợ',
  CUSTOMER: 'Quản lý Khách hàng',
  CUSTOMER_OBJECT: 'Quản lý khách hàng - Người dùng',
  ORDER: 'Quản lý khách hàng - Đơn hàng',
  RECEIPT: 'Quản lý khách hàng - Thanh toán',
  PRODUCT: 'Thiết lập Vật tư',
  PRODUCT_CATEGORY: 'Thiết lập Danh mục vật tư',
  SERVICE: 'Thiết lập Dịch vụ',
  SERVICE_CATEGORY: 'Thiết lập Nhóm dịch vụ',
  INVENTORY_DOC: 'Xuất/Nhập Vật tư',
  SHIFT_MANAGEMENT: 'Giao ca - Quản lý ca',
  SHIFT_MEMBERSHIP: 'Giao ca - Nhân sự ca',
  EXPENSE_VOUCHER: 'Quản lý chi phí',
  EXPENSE_CATEGORY: 'Thiết lập loại chi phí',
  PUMP: 'Thiết lập Công tơ lít',
  ACCOUNT_MANAGEMENT: 'Quản lý tài khoản',
  HISTORY: 'Lịch sử hệ thống',
};

export type Feature = (typeof DEFAULT_FEATURES)[number];

const pad = (num: number, size: number = 2) => String(num).padStart(size, '0');
const normalizeMilliseconds = (milliseconds: string = '0') =>
  Number(milliseconds.slice(0, 3).padEnd(3, '0'));

const getLocalDateParts = (date: Date) => ({
  year: date.getFullYear(),
  month: date.getMonth() + 1,
  day: date.getDate(),
  hour: date.getHours(),
  minute: date.getMinutes(),
  second: date.getSeconds(),
  millisecond: date.getMilliseconds(),
});

const getUtcDateParts = (date: Date) => ({
  year: date.getUTCFullYear(),
  month: date.getUTCMonth() + 1,
  day: date.getUTCDate(),
  hour: date.getUTCHours(),
  minute: date.getUTCMinutes(),
  second: date.getUTCSeconds(),
  millisecond: date.getUTCMilliseconds(),
});

const buildLocalDate = (
  year: number,
  month: number,
  day: number,
  hour: number = 0,
  minute: number = 0,
  second: number = 0,
  millisecond: number = 0,
) => new Date(year, month - 1, day, hour, minute, second, millisecond);

const buildVietnamISOStringFromParts = ({
  year,
  month,
  day,
  hour = 0,
  minute = 0,
  second = 0,
  millisecond = 0,
}: {
  year: number;
  month: number;
  day: number;
  hour?: number;
  minute?: number;
  second?: number;
  millisecond?: number;
}) =>
  new Date(
    Date.UTC(
      year,
      month - 1,
      day,
      hour - VIETNAM_TIMEZONE_OFFSET_HOURS,
      minute,
      second,
      millisecond,
    ),
  ).toISOString();

const parseLocalDateParts = (dateInput?: string) => {
  if (!dateInput) return null;

  const dateOnlyMatched = dateInput.match(LOCAL_DATE_ONLY_REGEX);
  if (dateOnlyMatched) {
    const [, year, month, day] = dateOnlyMatched;
    return {
      year: Number(year),
      month: Number(month),
      day: Number(day),
      hour: 0,
      minute: 0,
      second: 0,
      millisecond: 0,
    };
  }

  const dateTimeMatched = dateInput.match(LOCAL_DATE_TIME_REGEX);
  if (dateTimeMatched) {
    const [, year, month, day, hour, minute, second = '00', ms = '0'] = dateTimeMatched;
    return {
      year: Number(year),
      month: Number(month),
      day: Number(day),
      hour: Number(hour),
      minute: Number(minute),
      second: Number(second),
      millisecond: normalizeMilliseconds(ms),
    };
  }

  return null;
};

const formatDateParts = (
  parts: {
    year: number;
    month: number;
    day: number;
    hour?: number;
    minute?: number;
    second?: number;
  },
  onlyDate: boolean = false,
) => {
  const yyyy = parts.year;
  const mm = pad(parts.month);
  const dd = pad(parts.day);
  const hh = pad(parts.hour || 0);
  const mi = pad(parts.minute || 0);

  if (onlyDate) return `${dd}/${mm}/${yyyy}`;
  return `${dd}/${mm}/${yyyy} ${hh}:${mi}`;
};

export const convertTimestampToDate = (timestamp: number, isDatetime?: boolean) => {
  const date = new Date(timestamp * 1000 + VIETNAM_TIMEZONE_OFFSET_MS);
  return (
    date.getUTCFullYear() +
    '-' +
    pad(date.getUTCMonth() + 1) +
    '-' +
    pad(date.getUTCDate()) +
    `${
      isDatetime
        ? ` ${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}:${pad(date.getUTCSeconds())}`
        : ''
    }`
  );
};

export const parseDate = (dateStr: string) => {
  const localParts = parseLocalDateParts(dateStr);
  if (localParts) {
    return buildLocalDate(
      localParts.year,
      localParts.month,
      localParts.day,
      localParts.hour,
      localParts.minute,
      localParts.second,
      localParts.millisecond,
    );
  }

  if (ISO_WITH_TIMEZONE_REGEX.test(dateStr)) {
    const date = getVietnamShiftedDate(dateStr);
    if (date) {
      const parts = getUtcDateParts(date);
      return buildLocalDate(
        parts.year,
        parts.month,
        parts.day,
        parts.hour,
        parts.minute,
        parts.second,
        parts.millisecond,
      );
    }
  }

  const [day, month, year] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day);
};

export const parseISOStringPreserveLocalTime = (dateInput?: Date | string | number) => {
  if (dateInput === null || dateInput === undefined || dateInput === '') return null;
  if (dateInput instanceof Date) return new Date(dateInput);
  if (typeof dateInput === 'number') {
    const date = new Date(dateInput);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const matched = dateInput.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,9}))?)?)?(?:Z|[+-]\d{2}:?\d{2})?$/,
  );

  if (matched) {
    const [, year, month, day, hour = '00', minute = '00', second = '00', ms = '0'] = matched;
    return new Date(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hour),
      Number(minute),
      Number(second),
      normalizeMilliseconds(ms),
    );
  }

  const date = new Date(dateInput);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const convertStringToDate = (dateStr: Date | string, onlyDate: boolean = false) => {
  if (!dateStr) return '';
  const date = typeof dateStr === 'string' ? parseDate(dateStr) : dateStr;
  if (Number.isNaN(date.getTime())) return '';

  return formatDateParts(getLocalDateParts(date), onlyDate);
};

const getValidDate = (dateInput?: Date | string | number) => {
  if (dateInput === null || dateInput === undefined || dateInput === '') return null;

  const date = dateInput instanceof Date ? new Date(dateInput) : new Date(dateInput);
  if (Number.isNaN(date.getTime())) return null;

  return date;
};

const getVietnamShiftedDate = (dateInput?: Date | string | number) => {
  const date = getValidDate(dateInput);
  if (!date) return null;

  return new Date(date.getTime() + VIETNAM_TIMEZONE_OFFSET_MS);
};

export const toVietnamDateInputValue = (dateInput?: Date | string | number) => {
  if (typeof dateInput === 'string') {
    const localParts = parseLocalDateParts(dateInput);
    if (localParts) {
      return `${localParts.year}-${pad(localParts.month)}-${pad(localParts.day)}`;
    }
  }

  const date = getVietnamShiftedDate(dateInput);
  if (!date) return '';

  const yyyy = date.getUTCFullYear();
  const mm = pad(date.getUTCMonth() + 1);
  const dd = pad(date.getUTCDate());

  return `${yyyy}-${mm}-${dd}`;
};

export const toVietnamDateTimeLocalValue = (dateInput?: Date | string | number) => {
  if (typeof dateInput === 'string') {
    const localParts = parseLocalDateParts(dateInput);
    if (localParts) {
      return `${localParts.year}-${pad(localParts.month)}-${pad(localParts.day)}T${pad(localParts.hour)}:${pad(localParts.minute)}`;
    }
  }

  const date = getVietnamShiftedDate(dateInput);
  if (!date) return '';

  const yyyy = date.getUTCFullYear();
  const mm = pad(date.getUTCMonth() + 1);
  const dd = pad(date.getUTCDate());
  const hh = pad(date.getUTCHours());
  const mi = pad(date.getUTCMinutes());

  return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
};

export const toVietnamISOString = (dateInput?: Date | string | number) => {
  if (typeof dateInput === 'string') {
    const localParts = parseLocalDateParts(dateInput);
    if (localParts) {
      return buildVietnamISOStringFromParts(localParts);
    }
  }

  const date = getValidDate(dateInput);
  if (!date) return '';

  return date.toISOString();
};

export const timeToISOStringDate = (
  baseDate: Date,
  timeStr: string | undefined,
  defaultH: number = 0,
  defaultM: number = 0,
  dayOffset: number = 0,
) => {
  const d = new Date(baseDate);
  let h = defaultH;
  let m = defaultM;
  if (timeStr) {
    const parts = timeStr.split(':');
    if (parts.length >= 2) {
      h = Number(parts[0]) ?? defaultH;
      m = Number(parts[1]) ?? defaultM;
    }
  }
  d.setDate(d.getDate() + dayOffset);
  d.setHours(h, m, 0, 0);
  return toVietnamISOString(d);
};

export const timeStringToMinutes = (
  timeStr: string | undefined,
  defaultH: number = 0,
  defaultM: number = 0,
) => {
  let h = defaultH;
  let m = defaultM;

  if (timeStr) {
    const parts = timeStr.split(':');
    if (parts.length >= 2) {
      h = Number(parts[0]) ?? defaultH;
      m = Number(parts[1]) ?? defaultM;
    }
  }

  return h * 60 + m;
};

export const clearBlank = (obj: Object) => {
  return Object.fromEntries(Object.entries(obj).filter(([_, value]) => value !== ''));
};

export const isInRange = (dateStr: string, rangeStr: string) => {
  const date = getVietnamShiftedDate(dateStr);
  if (!date) return false;

  const hours = date.getUTCHours();
  const minutes = date.getUTCMinutes();

  const totalMinutes = hours * 60 + minutes;

  const [startStr, endStr] = rangeStr.split(' - ');
  const [startH, startM] = startStr.split(':').map(Number);
  const [endH, endM] = endStr.split(':').map(Number);

  const startMinutes = startH * 60 + startM;
  const endMinutes = endH * 60 + endM;

  return totalMinutes >= startMinutes && totalMinutes <= endMinutes;
};

export const formatCurrency = (value: number, currency: 'USD' | 'VND') => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    maximumFractionDigits: 2,
  }).format(value);
};

export const formatVND = (value: string | number) => {
  if (value === null || value === undefined || value === '') return '0';

  // keep early return for numeric zero
  if (value === 0) return '0';

  const str = value.toString();
  // allow optional leading minus, digits, commas and dots
  if (!/^-?[0-9.,]+$/.test(str)) return String(value);

  // normalize common thousand separators
  const normalized = str.replace(/,/g, '');
  const num = Number(normalized);
  if (Number.isNaN(num)) return String(value);

  const absInt = Math.trunc(Math.abs(num));
  if (absInt === 0) return '0';

  const formatted = new Intl.NumberFormat('vi-VN').format(absInt);
  return num < 0 ? `-${formatted}` : formatted;
};

export const isDateInRange = (dateStr: string, range: TRangeDate) => {
  const [day, month, year] = dateStr.split('/').map(Number);
  const date = new Date(year, month - 1, day);
  return date >= range.start && date <= range.end;
};

export const formatDateTime = (epochSeconds: number) => {
  const d = new Date(epochSeconds * 1000 + VIETNAM_TIMEZONE_OFFSET_MS);
  return d.toLocaleString('en-US', { hour12: true, timeZone: 'UTC' });
};

export function formatDate(date: Date): string {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
}

export function getWeekDays(week: TRangeDate) {
  if (!week.start) return [];
  const labels = ['Thứ 2', 'Thứ 3', 'Thứ 4', 'Thứ 5', 'Thứ 6', 'Thứ 7', 'CN'];

  const days: { key: string; label: string; date: string }[] = [];

  for (let i = 0; i < 7; i++) {
    const d = new Date(week.start);
    d.setDate(week.start.getDate() + i);
    d.setHours(0, 0, 0, 0);

    const dateStr = `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(
      2,
      '0',
    )}/${d.getFullYear()}`;

    days.push({
      key: labels[i],
      label: labels[i],
      date: dateStr,
    });
  }

  return days;
}

export const resizeToTargetSize = (
  file: File,
  maxWidth: number,
  maxHeight: number,
  targetSize: number = 200 * 1024,
): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();

    reader.onload = (e) => {
      img.src = e.target?.result as string;
    };

    img.onload = () => {
      const canvas = document.createElement('canvas');
      let { width, height } = img;

      // Giữ tỉ lệ ảnh
      if (width > height) {
        if (width > maxWidth) {
          height *= maxWidth / width;
          width = maxWidth;
        }
      } else {
        if (height > maxHeight) {
          width *= maxHeight / height;
          height = maxHeight;
        }
      }

      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx?.drawImage(img, 0, 0, width, height);

      let quality = 0.9;

      const tryCompress = () => {
        canvas.toBlob(
          (blob) => {
            if (!blob) return reject(new Error('Resize failed'));
            if (blob.size <= targetSize || quality <= 0.3) {
              resolve(blob);
            } else {
              quality -= 0.1; // giảm quality dần
              canvas.toBlob(
                (b) => {
                  if (!b) return reject(new Error('Resize failed'));
                  if (b.size <= targetSize || quality <= 0.3) {
                    resolve(b);
                  } else {
                    tryCompress();
                  }
                },
                'image/jpeg',
                quality,
              );
            }
          },
          'image/jpeg',
          quality,
        );
      };

      tryCompress();
    };

    reader.readAsDataURL(file);
  });
};

export async function getImage(url: string) {
  return await fetch(url, { method: 'GET' });
}

async function uploadImage(file: File, presignedUrl: string, headers: any) {
  await fetch(presignedUrl, {
    method: 'PUT',
    headers: { ...headers },
    body: file,
  });
}

export async function uploadFileWithModule(file: File, module: string = 'order') {
  try {
    const param = {
      fileName: file.name,
      fileSize: file.size,
      contentType: file.type,
      module,
    };
    const presign: any = await callGetPresignFromS3(param);
    await uploadImage(file, presign.url, presign.requiredHeaders);

    return presign?.key;
  } catch (err) {
    console.error('Upload error:', err);
    return '';
  }
}

export async function uploadFile(file: File) {
  return uploadFileWithModule(file, 'order');
}

export function validImages(imagesJson?: string) {
  if (!imagesJson) return;
  if (Array.isArray(imagesJson)) return imagesJson;
  const parsed = JSON.parse(imagesJson);
  return Array.isArray(parsed) ? parsed : [];
}

export function joinText(arr: any[]) {
  if (!Array.isArray(arr)) return '';

  const filtered = arr.filter((item) => item !== null && item !== undefined && item !== '');
  if (filtered.length === 0) return '';

  return filtered.join('-');
}

export function joinName(arr: any[]) {
  if (!Array.isArray(arr)) return '';
  return arr.filter(Boolean).join(' ').trim();
}

export function debounce<T extends (...args: any[]) => void>(fn: T, delay: number) {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;

  return function (this: ThisParameterType<T>, ...args: Parameters<T>) {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(() => {
      fn.apply(this, args);
    }, delay);
  };
}

export const getAllImagesFromObjects = (arr: any) => {
  return arr.flatMap((item: any) => {
    if (typeof item.imagesJson === 'string') {
      try {
        const parsed = JSON.parse(item.imagesJson);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    return [];
  });
};

export const replaceImagesJson = (arr: any, convertedImages: string[]) => {
  let index = 0;

  return arr.map((item: any) => {
    const oldImages =
      typeof item.imagesJson === 'string' ? JSON.parse(item.imagesJson || '[]') : [];
    const newImages = convertedImages.slice(index, index + oldImages.length);
    index += oldImages.length;

    return {
      ...item,
      s3Path: oldImages, // keep original s3Path for reference
      imagesJson: newImages,
    };
  });
};

export const decodeJwt = (token: string): any => {
  try {
    const parts = token.split('.');
    if (parts.length < 2) return null;
    const payload = parts[1];
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      window
        .atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join(''),
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    return null;
  }
};
